import { Divider, Flex, Stack, Text } from '@chakra-ui/react';
import { useEffect, useState } from 'react';

import AddedUpdatedForm from './automation-forms/AddedUpdatedForm';
import AssignmentBuilderFormContainer from '@/client/components/admin/assignments/builder/layout/AssignmentBuilderFormContainer';
import type { AssignmentFormData } from '../../../types/assignmentTypes';
import CompletedAssignmentForm from './automation-forms/CompletedAssignmentForm';
import CompletedContentForm from './automation-forms/CompletedContentForm';
import HiredDaysAgoForm from './automation-forms/HiredDaysAgoForm';
import WarmNeutralSelectionMenu from './WarmNeutralSelectionMenu';
import { WorkflowType } from '@/client/services/api/graphql/gql/graphql';
import WorkshopForm from './automation-forms/WorkshopForm';
import { useAdminAssignmentsStore } from '../../../state/adminAssignmentsStore';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export default function AssignmentAutomationsForm() {
  const { t } = useTranslation();
  const { setActiveForm } = useAdminAssignmentsStore();
  const {
    watch,
    setValue,
    formState: { errors },
    clearErrors,
  } = useFormContext<AssignmentFormData>();
  const { settings } = watch();
  const { type } = settings;

  const hasError = !!errors?.settings?.type;

  const [selectedFormComponent, setSelectedFormComponent] = useState<{
    label: string;
    formComponent: JSX.Element;
  } | null>(null);

  const formComponentTranslationMap = {
    addedUpdated: t('admin.assignments.automations.userAddedUpdated.label'),
    completedContent: t('admin.assignments.automations.contentCompleted.label'),
    completedAssignment: t('admin.assignments.automations.assignmentCompleted.label'),
    registeredForWorkshop: t('admin.assignments.automations.workshopRegisteredAttended.label'),
    hiredDaysAgo: t('admin.assignments.automations.hiredDaysAgo.label'),
  };

  const formComponentMap = {
    addedUpdated: {
      label: formComponentTranslationMap.addedUpdated,
      formComponent: <AddedUpdatedForm />,
    },
    completedContent: {
      label: formComponentTranslationMap.completedContent,
      formComponent: <CompletedContentForm />,
    },
    completedAssignment: {
      label: formComponentTranslationMap.completedAssignment,
      formComponent: <CompletedAssignmentForm />,
    },
    registeredForWorkshop: {
      label: formComponentTranslationMap.registeredForWorkshop,
      formComponent: <WorkshopForm />,
    },
    hiredDaysAgo: {
      label: formComponentTranslationMap.hiredDaysAgo,
      formComponent: <HiredDaysAgoForm />,
    },
  };

  const typeEqualsAddedUpdated =
    type === WorkflowType.UserAdded ||
    type === WorkflowType.UserUpdated ||
    type === WorkflowType.UserAddedOrUpdated;

  const typeEqualsCompletedContent = type === WorkflowType.CompletedContent;

  const typeEqualsCompletedAssignment = type === WorkflowType.CompletedAssignment;

  const typeEqualsRegisteredWorkshop =
    type === WorkflowType.AttendedWorkshop || type === WorkflowType.RegisteredWorkshop;

  const typeEqualsHiredDaysAgo =
    type === WorkflowType.HiredToday ||
    type === WorkflowType.Hired_60 ||
    type === WorkflowType.Hired_90 ||
    type === WorkflowType.HiredCustom;

  useEffect(() => {
    setActiveForm('automations');

    if (typeEqualsAddedUpdated) {
      setSelectedFormComponent(formComponentMap.addedUpdated);
    } else if (typeEqualsCompletedContent) {
      setSelectedFormComponent(formComponentMap.completedContent);
    } else if (typeEqualsCompletedAssignment) {
      setSelectedFormComponent(formComponentMap.completedAssignment);
    } else if (typeEqualsRegisteredWorkshop) {
      setSelectedFormComponent(formComponentMap.registeredForWorkshop);
    } else if (typeEqualsHiredDaysAgo) {
      setSelectedFormComponent(formComponentMap.hiredDaysAgo);
    }
  }, []);

  const clearTriggerObject = () => {
    setValue('settings.triggerObject', { id: null, title: '' });
    clearErrors('settings.triggerObject');
  };

  const automationOptions = [
    {
      label: formComponentTranslationMap.addedUpdated,
      helpText: t('admin.assignments.automations.userAddedUpdated.helpText'),
      onClick: () => {
        clearTriggerObject();
        setValue('settings.type', WorkflowType.UserAdded);

        setSelectedFormComponent(formComponentMap.addedUpdated);
      },
    },
    {
      label: formComponentTranslationMap.completedContent,
      helpText: t('admin.assignments.automations.contentCompleted.helpText'),
      onClick: () => {
        if (type !== WorkflowType.CompletedContent) {
          clearTriggerObject();
          setValue('settings.type', WorkflowType.CompletedContent);
        }

        setSelectedFormComponent(formComponentMap.completedContent);
      },
    },
    {
      label: formComponentTranslationMap.completedAssignment,
      helpText: t('admin.assignments.automations.assignmentCompleted.helpText'),
      onClick: () => {
        if (type !== WorkflowType.CompletedAssignment) {
          clearTriggerObject();
          setValue('settings.type', WorkflowType.CompletedAssignment);
        }

        setSelectedFormComponent(formComponentMap.completedAssignment);
      },
    },
    {
      label: formComponentTranslationMap.registeredForWorkshop,
      helpText: t('admin.assignments.automations.workshopRegisteredAttended.helpText'),
      onClick: () => {
        if (type !== WorkflowType.AttendedWorkshop && type !== WorkflowType.RegisteredWorkshop) {
          clearTriggerObject();
          setValue('settings.type', WorkflowType.RegisteredWorkshop);
        }

        setSelectedFormComponent(formComponentMap.registeredForWorkshop);
      },
    },
    {
      label: formComponentTranslationMap.hiredDaysAgo,
      helpText: t('admin.assignments.automations.hiredDaysAgo.helpText'),
      onClick: () => {
        clearTriggerObject();
        setSelectedFormComponent(formComponentMap.hiredDaysAgo);

        setValue('settings.type', WorkflowType.HiredToday);
      },
    },
  ];

  return (
    <AssignmentBuilderFormContainer
      headerComponent={
        <Flex justifyContent="space-between" alignItems="center">
          <Text fontSize="24px" fontWeight={600}>
            {t('admin.assignments.automations.selectAutomation')}
          </Text>
        </Flex>
      }
      formComponent={
        <Stack marginTop={6} spacing={8}>
          <WarmNeutralSelectionMenu
            hasError={hasError}
            label="Select Automation Type"
            buttonLabel={
              selectedFormComponent
                ? selectedFormComponent.label
                : t('admin.assignments.automations.selectAutomation')
            }
            options={automationOptions}
          />

          {selectedFormComponent && (
            <>
              <Divider />

              {selectedFormComponent.formComponent}
            </>
          )}
        </Stack>
      }
    />
  );
}
